// What a message row can do, wired once for the conversation and the thread pane alike (the Mac's
// full context menu: react, reply in thread, copy, copy link, pin, save, mark unread, edit, delete).
import { useMemo } from 'react';
import { data } from '../../bridge/client';
import { t } from '../../i18n/i18n';
import { permalinkUrl } from '../router';
import type { RowActions } from './MessageRow';
import { rememberEmoji } from '../features/emoji/recents';
import { openForward } from '../features/compose/composeSignals';

/** The shared recents (features/emoji/recents.ts — Apple's `emoji_recents`), so the hover bar learns. */
export function rememberReaction(name: string): void {
  rememberEmoji(name);
}

export function useRowActions(o: {
  onOpenThread(rootId: string): void;
  onOpenUserId(userId: string, anchor?: { x: number; y: number }): void;
  onToast(message: string, tone?: 'info' | 'error'): void;
  teamName: string;
  quickReactions: string[];
}): RowActions {
  return useMemo(
    () => ({
      onReact: (postId, name) => {
        rememberReaction(name);
        void data.call('toggleReaction', postId, name);
      },
      onOpenThread: o.onOpenThread,
      onOpenUserId: o.onOpenUserId,
      onRetry: (postId) => void data.call('retryFailed', postId),
      onDiscard: (postId) => void data.call('discardFailed', postId),
      onAction: (postId, actionId, cookie, option) =>
        void data.call('postAction', postId, actionId, cookie, option).then((ok) => {
          if (!ok) o.onToast(t('channel.action.failed'), 'error');
        }),
      onCopyText: (text) => {
        void navigator.clipboard?.writeText(text).then(() => o.onToast(t('web.msg.copied')));
      },
      onCopyLink: (postId) => {
        void navigator.clipboard?.writeText(permalinkUrl(o.teamName, postId)).then(() => o.onToast(t('web.msg.linkCopied')));
      },
      onPin: (postId, pinned) =>
        void data.callUntyped<boolean>('setPostPinned', postId, pinned).then(
          (ok) => ok === false && o.onToast(t('channel.action.failed'), 'error'),
          () => o.onToast(t('channel.action.failed'), 'error'),
        ),
      onSave: (postId, saved) =>
        void data.callUntyped<boolean>('setPostSaved', postId, saved).then(
          (ok) => ok === false && o.onToast(t('channel.action.failed'), 'error'),
          () => o.onToast(t('channel.action.failed'), 'error'),
        ),
      onMarkUnread: (postId) =>
        void data.call('markUnreadFromPost', postId).then((ok) => o.onToast(ok ? t('web.msg.markedUnread') : t('channel.action.failed'), ok ? 'info' : 'error')),
      onEdit: async (postId, message) => {
        const r = await data.call('editPost', postId, message);
        if (!r.ok) o.onToast(r.message || t('channel.action.failed'), 'error');
        return r.ok;
      },
      onDelete: (postId) =>
        void data.call('deletePost', postId).then((r) => {
          if (!r.ok) o.onToast(r.message || t('channel.action.failed'), 'error');
        }),
      onForward: (post, authorName) => openForward({ post, authorName }),
      quickReactions: o.quickReactions,
    }),
    [o.onOpenThread, o.onOpenUserId, o.onToast, o.teamName, o.quickReactions],
  );
}
